import { EmployeeAccount } from "../components/EmployeeAccount"
import { type FormEvent, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import {
  Button, Card, Empty, ErrorNote, Field, PageHeader, Spinner, formatDate, formatDateTime, useLoad,
  useToast,
} from "../components/ui"
import {
  type AdminActionEntry,
  type PlatformCompany,
  type PlatformCompanyDetail,
  type PlatformEmployee,
  type PlatformMembership,
  type PlatformModel,
  type PlatformReport,
  api,
} from "../lib/api"
import { useAuth } from "../lib/auth"

// Moderniser staff screens. Everything here talks to /platform, which answers
// 404 to anyone who is not platform_admin — the nav and the route guard hide
// it, the API is what enforces it.

const money = (micro: number | undefined) => `$${((micro ?? 0) / 1e6).toFixed(4)}`
const perMtok = (usd: number) => `$${usd.toFixed(usd >= 1 ? 2 : 4)}`

// The three staff-switchable capabilities. Mirrors FLAGS in
// cloud/src/platform/flags.ts — absent means on.
const FLAG_LABELS: Record<string, string> = {
  "contextHub.connectors": "Context Hub connectors (imports, credentials)",
  "contextHub.embedding": "Context Hub embedding (semantic search)",
  "connections.sap": "SAP connection editing",
}

// ---------- Companies ----------

export function PlatformCompaniesPage() {
  const companies = useLoad(() => api.get<{ companies: PlatformCompany[] }>("/platform/companies"))
  const [creating, setCreating] = useState(false)
  const { switchCompany } = useAuth()
  const navigate = useNavigate()

  return (
    <>
      <PageHeader
        title="Companies"
        subtitle="Every tenant, with credits, margin and what they earn us."
        action={
          <Button onClick={() => setCreating((v) => !v)}>{creating ? "Cancel" : "New company"}</Button>
        }
      />
      {creating && (
        <CreateCompanyForm
          onDone={() => {
            setCreating(false)
            companies.reload()
          }}
        />
      )}
      <ErrorNote error={companies.error} />
      {companies.loading ? (
        <Spinner />
      ) : companies.data?.companies.length ? (
        <div className="flex flex-col gap-2">
          {companies.data.companies.map((co) => (
            <Card key={co.id} className="flex items-center gap-4 p-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Link to={`/platform/companies/${co.id}`} className="truncate font-medium text-foreground hover:text-accent">
                    {co.name}
                  </Link>
                  {co.suspendedAt && (
                    <span className="rounded border border-danger/30 bg-danger-soft px-1.5 py-0.5 text-xs text-danger">
                      suspended
                    </span>
                  )}
                  {!co.billingEnforced && (
                    <span className="rounded border border-warning/30 bg-warning-soft px-1.5 py-0.5 text-xs text-warning">
                      billing off
                    </span>
                  )}
                </div>
                <p className="truncate text-xs text-muted">
                  {co.domain} · {co.records} requests · margin {co.marginPercent}
                </p>
              </div>
              <div className="hidden text-right sm:block">
                <p className="font-mono text-sm tabular-nums text-foreground">{co.creditsFormatted}</p>
                <p className="text-xs text-muted">credits</p>
              </div>
              <div className="hidden text-right sm:block">
                <p className="font-mono text-sm tabular-nums text-success">{co.grossProfitFormatted}</p>
                <p className="text-xs text-muted">profit</p>
              </div>
              {/* Two different things, and they used to be one button. Open
                  reads the account; Act as puts you inside it. */}
              <Button variant="ghost" onClick={() => navigate(`/platform/companies/${co.id}`)}>
                Open
              </Button>
              <Button
                variant="subtle"
                title={`Work as ${co.name} — you will see exactly what their people see, until you switch back`}
                onClick={async () => {
                  await switchCompany(co.id)
                  navigate("/")
                }}
              >
                Act as
              </Button>
            </Card>
          ))}
        </div>
      ) : (
        <Empty title="No companies yet" />
      )}
    </>
  )
}

function CreateCompanyForm({ onDone }: { onDone: () => void }) {
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const dollars = Number(form.get("credits") || 0)
    setBusy(true)
    setError(null)
    try {
      await api.post("/platform/companies", {
        name: form.get("name"),
        domain: form.get("domain"),
        initialCreditsCents: Math.round(dollars * 100),
      })
      onDone()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="p-5">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Company name" name="name" required />
        <Field label="Domain" name="domain" placeholder="acme.com" required />
        <Field
          label="Starting credit (USD)"
          name="credits"
          type="number"
          min="0"
          step="1"
          defaultValue="0"
          hint="Recorded as a grant in their purchase history."
        />
        <p className="text-xs text-muted">
          Then open the company and invite its first admin — the invite flow works for staff.
        </p>
        <ErrorNote error={error} />
        <div>
          <Button type="submit" disabled={busy}>{busy ? "Creating…" : "Create company"}</Button>
        </div>
      </form>
    </Card>
  )
}

// ---------- Company detail ----------

export function PlatformCompanyDetailPage() {
  const { companyId = "" } = useParams()
  const navigate = useNavigate()
  const detail = useLoad(
    () => api.get<PlatformCompanyDetail>(`/platform/companies/${companyId}`),
    [companyId],
  )
  const audit = useLoad(
    () => api.get<{ actions: AdminActionEntry[] }>(`/platform/audit?companyId=${companyId}&limit=50`),
    [companyId],
  )
  const [error, setError] = useState<unknown>(null)

  const co = detail.data?.company
  const totals = detail.data?.totals

  async function act(run: () => Promise<unknown>) {
    setError(null)
    try {
      await run()
      detail.reload()
      audit.reload()
    } catch (err) {
      setError(err)
    }
  }

  if (detail.loading) return <Spinner />
  if (!co) return <ErrorNote error={detail.error ?? new Error("Company not found")} />

  return (
    <>
      <PageHeader
        title={co.name}
        eyebrow={co.domain}
        subtitle={`Created ${formatDate(co.createdAt)}`}
        action={
          <div className="flex gap-2">
            <ActAsCompany company={co} />
            {co.suspendedAt ? (
              <Button onClick={() => act(() => api.post(`/platform/companies/${co.id}/unsuspend`))}>
                Unsuspend
              </Button>
            ) : (
              <Button
                variant="danger"
                onClick={() => act(() => api.post(`/platform/companies/${co.id}/suspend`))}
              >
                Suspend
              </Button>
            )}
          </div>
        }
      />
      {co.suspendedAt && (
        <ErrorNote error={new Error(`Suspended since ${formatDateTime(co.suspendedAt)} — everyone but staff is locked out.`)} />
      )}
      <ErrorNote error={error} />

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Credits" value={co.creditsFormatted} />
        <Stat label="Charged" value={totals?.chargedFormatted ?? "—"} />
        <Stat label="Cost" value={totals?.costFormatted ?? "—"} />
        <Stat label="Profit" value={totals?.grossProfitFormatted ?? "—"} />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <CompanyDetailsCard company={co} onSaved={() => act(async () => {})} />
        <MarginCard companyId={co.id} marginBps={co.marginBps} onChanged={() => act(async () => {})} />
        <GrantCreditsCard companyId={co.id} onChanged={() => act(async () => {})} />
      </div>

      <Card className="flex flex-col gap-3 p-5">
        <h2 className="font-semibold text-foreground">Billing & features</h2>
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={co.billingEnforced}
            onChange={(e) =>
              act(() => api.patch(`/platform/companies/${co.id}/billing`, { enforced: e.target.checked }))
            }
          />
          Billing enforced
          <span className="text-xs text-muted">— off lets them keep working at zero balance</span>
        </label>
        {Object.keys(FLAG_LABELS).map((flag) => (
          <label key={flag} className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={co.featureFlags?.[flag] !== false}
              onChange={(e) =>
                act(() => api.patch(`/platform/companies/${co.id}/flags`, { flags: { [flag]: e.target.checked } }))
              }
            />
            {FLAG_LABELS[flag]}
          </label>
        ))}
      </Card>

      <section className="flex flex-col gap-2">
        <h2 className="font-semibold text-foreground">Recent staff actions</h2>
        <AuditList actions={audit.data?.actions ?? []} loading={audit.loading} />
      </section>

      <Card className="flex items-center justify-between gap-4 border-danger/30 p-5">
        <div>
          <p className="text-sm font-medium text-foreground">Delete this company</p>
          <p className="text-xs text-muted">
            Only an empty shell can go — anything with usage, credit or content is refused. Suspend
            instead.
          </p>
        </div>
        <Button
          variant="danger"
          onClick={() => {
            if (!window.confirm(`Delete ${co.name} (${co.domain})? This cannot be undone.`)) return
            void act(async () => {
              await api.delete(`/platform/companies/${co.id}`)
              navigate("/platform/companies")
            })
          }}
        >
          Delete
        </Button>
      </Card>
    </>
  )
}

/**
 * Name and domain.
 *
 * The domain is not cosmetic: it is what an invitation is checked against and
 * how a signup finds an existing tenant, so changing it changes who can join.
 * Saying so beside the field is cheaper than the support thread afterwards.
 */
function CompanyDetailsCard({
  company,
  onSaved,
}: {
  company: PlatformCompanyDetail["company"]
  onSaved: () => void
}) {
  const toast = useToast()
  const [name, setName] = useState(company.name)
  const [domain, setDomain] = useState(company.domain)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const dirty = name !== company.name || domain !== company.domain

  async function save() {
    setSaving(true)
    setError(null)
    try {
      await api.patch(`/platform/companies/${company.id}`, { name, domain })
      toast("Company updated")
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-4">
      <h2 className="text-sm font-semibold text-foreground">Details</h2>
      <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <Field
        label="Domain"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        hint="Used to match invitations and signups to this tenant. Changing it changes who can join."
      />
      <ErrorNote error={error} />
      <div className="flex gap-2">
        <Button onClick={() => void save()} disabled={!dirty || saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
        {dirty && (
          <Button
            variant="subtle"
            onClick={() => {
              setName(company.name)
              setDomain(company.domain)
            }}
            disabled={saving}
          >
            Reset
          </Button>
        )}
      </div>
    </Card>
  )
}

/**
 * Steps into the tenant.
 *
 * Separate from reading the account on purpose — the two were one button, so
 * looking someone up put you inside their company without saying so. From here
 * every screen is theirs until the switcher is used to come back, which the
 * layout already announces with a banner.
 */
function ActAsCompany({ company }: { company: PlatformCompanyDetail["company"] }) {
  const { switchCompany } = useAuth()
  const navigate = useNavigate()

  return (
    <Button
      variant="subtle"
      title={`See exactly what ${company.name} sees. Switch back from the company picker in the sidebar.`}
      onClick={async () => {
        await switchCompany(company.id)
        navigate("/")
      }}
    >
      Act as
    </Button>
  )
}

function MarginCard({
  companyId,
  marginBps,
  onChanged,
}: {
  companyId: string
  marginBps: number
  onChanged: () => void
}) {
  const [value, setValue] = useState(String(marginBps))
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)
  const pct = Number(value) / 100

  return (
    <Card className="flex flex-col gap-3 p-5">
      <h2 className="font-semibold text-foreground">Margin</h2>
      <p className="text-xs text-muted">
        Share of every charge that is ours, in basis points. 2000 = 20% of price. Applies to future
        usage only.
      </p>
      <div className="flex items-end gap-2">
        <Field
          label="Basis points"
          name="marginBps"
          type="number"
          min="0"
          max="9000"
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            setSaved(false)
          }}
          hint={Number.isFinite(pct) ? `${pct.toFixed(2)}% margin` : undefined}
        />
        <Button
          className="mb-6"
          onClick={async () => {
            setError(null)
            try {
              await api.patch(`/platform/companies/${companyId}/margin`, { marginBps: Number(value) })
              setSaved(true)
              onChanged()
            } catch (err) {
              setError(err)
            }
          }}
        >
          Save
        </Button>
      </div>
      {saved && <p className="text-sm text-success">Margin updated.</p>}
      <ErrorNote error={error} />
    </Card>
  )
}

function GrantCreditsCard({ companyId, onChanged }: { companyId: string; onChanged: () => void }) {
  const [error, setError] = useState<unknown>(null)
  const [granted, setGranted] = useState<string | null>(null)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const dollars = Number(form.get("amount"))
    setError(null)
    setGranted(null)
    try {
      const res = await api.post<{ creditsFormatted: string }>(
        `/platform/companies/${companyId}/credits`,
        { amountCents: Math.round(dollars * 100), note: form.get("note") || undefined },
      )
      setGranted(res.creditsFormatted)
      e.currentTarget?.reset?.()
      onChanged()
    } catch (err) {
      setError(err)
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-5">
      <h2 className="font-semibold text-foreground">Gift credits</h2>
      <p className="text-xs text-muted">
        Lands in their purchase history as a grant, with your name on it in the audit log.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="flex items-end gap-2">
          <Field label="Amount (USD)" name="amount" type="number" min="1" step="1" required />
          <Button type="submit" className="mb-0.5">Grant</Button>
        </div>
        <Field label="Note" name="note" placeholder="Why — e.g. onboarding, goodwill" />
      </form>
      {granted && <p className="text-sm text-success">Done — balance is now {granted}.</p>}
      <ErrorNote error={error} />
    </Card>
  )
}

// ---------- People ----------

export function PlatformPeoplePage() {
  const [q, setQ] = useState("")
  const people = useLoad(
    () => api.get<{ employees: PlatformEmployee[] }>(`/platform/employees?q=${encodeURIComponent(q)}`),
    [q],
  )
  const [open, setOpen] = useState<string | null>(null)

  return (
    <>
      <PageHeader title="People" subtitle="Everyone on the platform, across every company." />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search name or email…"
        className="rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none placeholder:text-faint focus:border-interactive"
      />
      <ErrorNote error={people.error} />
      {people.loading ? (
        <Spinner />
      ) : people.data?.employees.length ? (
        <div className="flex flex-col gap-2">
          {people.data.employees.map((p) => (
            <Card key={p.id} className="flex flex-col p-4">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-4 text-left"
                onClick={() => setOpen(open === p.id ? null : p.id)}
              >
                <div className="min-w-0">
                  <p className="truncate text-foreground">{p.name}</p>
                  <p className="truncate text-xs text-muted">
                    {p.email} · {p.memberships.map((m) => m.companyName).join(", ") || "no company"}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {p.status !== "active" && (
                    <span className="rounded border border-danger/30 bg-danger-soft px-1.5 py-0.5 text-xs text-danger">
                      {p.status}
                    </span>
                  )}
                  <span className="rounded border border-accent/30 bg-accent-soft px-1.5 py-0.5 text-xs text-accent">
                    {p.role}
                  </span>
                </div>
              </button>
              {open === p.id && <PersonPanel person={p} onChanged={people.reload} />}
            </Card>
          ))}
        </div>
      ) : (
        <Empty title="Nobody matches" />
      )}
    </>
  )
}

function PersonPanel({ person, onChanged }: { person: PlatformEmployee; onChanged: () => void }) {
  const [error, setError] = useState<unknown>(null)
  const isStaff = person.role === "platform_admin"

  async function act(run: () => Promise<unknown>) {
    setError(null)
    try {
      await run()
      onChanged()
    } catch (err) {
      setError(err)
    }
  }

  return (
    <div className="mt-4 flex flex-col gap-4 border-t border-border pt-4">
      <ErrorNote error={error} />
      {person.status === "invited" ? (
        <p className="text-xs text-muted">
          Account details can be edited after this person accepts their invitation.
        </p>
      ) : (
        <EmployeeAccount employee={person} scope="platform" onSaved={onChanged} />
      )}

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-foreground">Companies & roles</h3>
        {person.memberships.map((m) => {
          // The last one can go too: someone left in no company is disabled,
          // the same rule company deletion and a company admin's removal follow.
          const onlyCompany = person.memberships.length === 1
          return (
            <div key={m.companyId} className="flex flex-col gap-2 rounded border border-border p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-muted">{m.companyName}</span>
                <div className="flex shrink-0 items-center gap-3">
                  {m.status === "disabled" && (
                    <span className="rounded border border-warning/30 bg-warning-soft px-1.5 py-0.5 text-xs text-warning">
                      access off
                    </span>
                  )}
                  {isStaff ? (
                    <span className="text-xs text-muted">{m.role}</span>
                  ) : (
                    <select
                      value={m.role}
                      onChange={(e) =>
                        act(() =>
                          api.patch(`/platform/employees/${person.id}/role`, {
                            companyId: m.companyId,
                            role: e.target.value,
                          }),
                        )
                      }
                      className="rounded border border-border bg-surface px-2 py-1 text-xs outline-none focus:border-interactive"
                    >
                      <option value="developer">developer</option>
                      <option value="external">external</option>
                      <option value="admin">admin</option>
                    </select>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const note = onlyCompany
                        ? "It is their only company, so their account will be disabled."
                        : "Their work there stays; they just stop being able to act for it."
                      if (!window.confirm(`Remove ${person.name} from ${m.companyName}? ${note}`)) return
                      act(() => api.delete(`/platform/employees/${person.id}/memberships/${m.companyId}`))
                    }}
                    className="text-xs text-muted underline-offset-2 hover:text-danger hover:underline"
                  >
                    remove
                  </button>
                </div>
              </div>
              {!isStaff && <SpendCaps personId={person.id} membership={m} onSaved={onChanged} />}
            </div>
          )
        })}
        {!isStaff && <AddMembership person={person} onAdded={onChanged} />}
        {isStaff && (
          <p className="text-xs text-faint">
            Staff act for every company; a membership only decides which ones they land in without choosing.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {!isStaff &&
          (person.status === "disabled" ? (
            <Button onClick={() => act(() => api.patch(`/platform/employees/${person.id}`, { status: "active" }))}>
              Enable account
            </Button>
          ) : (
            <Button
              variant="danger"
              onClick={() => act(() => api.patch(`/platform/employees/${person.id}`, { status: "disabled" }))}
            >
              Disable account
            </Button>
          ))}
        <Button
          variant="ghost"
          onClick={() => {
            if (!window.confirm(`Log ${person.name} out everywhere and revoke their API tokens?`)) return
            void act(() => api.post(`/platform/employees/${person.id}/revoke-sessions`))
          }}
        >
          Force log-out
        </Button>
      </div>
      {isStaff && (
        <p className="text-xs text-muted">
          Platform staff roles must be changed by the team that manages the platform.
        </p>
      )}
    </div>
  )
}

/**
 * Puts someone in another company.
 *
 * The whole point of memberships: one login, several clients, a separate role
 * in each. The company list is loaded here rather than passed down because it
 * is only needed once someone opens a person, and there can be a lot of them.
 */
function AddMembership({ person, onAdded }: { person: PlatformEmployee; onAdded: () => void }) {
  const [open, setOpen] = useState(false)
  const [companyId, setCompanyId] = useState("")
  const [role, setRole] = useState("developer")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const companies = useLoad(
    () => (open ? api.get<{ companies: { id: string; name: string }[] }>("/platform/companies") : Promise.resolve(null)),
    [open],
  )

  const already = new Set(person.memberships.map((m) => m.companyId))
  const available = (companies.data?.companies ?? []).filter((c) => !already.has(c.id))

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start text-xs text-interactive underline-offset-2 hover:underline"
      >
        + Add to a company
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-2 rounded border border-border bg-surface-muted p-3">
      <ErrorNote error={error} />
      {companies.loading ? (
        <p className="text-xs text-muted">Loading companies…</p>
      ) : available.length === 0 ? (
        <p className="text-xs text-muted">They are already in every company.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="rounded border border-border bg-surface px-2 py-1 text-xs outline-none focus:border-interactive"
          >
            <option value="">Choose a company…</option>
            {available.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="rounded border border-border bg-surface px-2 py-1 text-xs outline-none focus:border-interactive"
          >
            <option value="developer">developer</option>
            <option value="external">external</option>
            <option value="admin">admin</option>
          </select>
          <Button
            disabled={!companyId || busy}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                await api.post(`/platform/employees/${person.id}/memberships`, { companyId, role })
                setOpen(false)
                setCompanyId("")
                onAdded()
              } catch (err) {
                setError(err)
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? "Adding…" : "Add"}
          </Button>
          <Button variant="subtle" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
        </div>
      )}
      <p className="text-xs text-faint">
        The role applies to that company only. They pick which company they are working in from the
        switcher at the top of the sidebar.
      </p>
    </div>
  )
}

/** Caps on one membership: what this person may spend of that company's credits. */
function SpendCaps({ personId, membership, onSaved }: { personId: string; membership: PlatformMembership; onSaved: () => void }) {
  const toUsd = (micro: number | null) => (micro == null ? "" : String(micro / 1e6))
  const [caps, setCaps] = useState({
    day: toUsd(membership.spendCap24hMicroUsd),
    week: toUsd(membership.spendCap7dMicroUsd),
    month: toUsd(membership.spendCap30dMicroUsd),
  })
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)

  const toMicro = (v: string) => {
    if (v.trim() === "") return null
    const n = Number(v)
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 1e6) : null
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-muted">Spend caps on {membership.companyName}'s credits (USD, blank = none)</p>
      <div className="flex flex-wrap items-end gap-2">
        {(
          [
            ["day", "Per day"],
            ["week", "Per 7 days"],
            ["month", "Per 30 days"],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="flex flex-col gap-1 text-xs text-muted">
            {label}
            <input
              type="number"
              min="0"
              step="1"
              value={caps[key]}
              onChange={(e) => {
                setCaps({ ...caps, [key]: e.target.value })
                setSaved(false)
              }}
              className="w-28 rounded border border-border bg-surface px-2 py-1.5 text-sm text-foreground outline-none focus:border-interactive"
            />
          </label>
        ))}
        <Button
          variant="ghost"
          onClick={async () => {
            setError(null)
            try {
              await api.patch(`/platform/employees/${personId}/memberships/${membership.companyId}/spend-caps`, {
                spendCap24hMicroUsd: toMicro(caps.day),
                spendCap7dMicroUsd: toMicro(caps.week),
                spendCap30dMicroUsd: toMicro(caps.month),
              })
              setSaved(true)
              onSaved()
            } catch (err) {
              setError(err)
            }
          }}
        >
          Save caps
        </Button>
      </div>
      {saved && <p className="text-xs text-success">Caps saved.</p>}
      <ErrorNote error={error} />
    </div>
  )
}

// ---------- Rates & models ----------

export function PlatformModelsPage() {
  const models = useLoad(() => api.get<{ models: PlatformModel[] }>("/platform/models"))
  const [marginBps, setMarginBps] = useState(2000)
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  // charged = cost / (1 - margin) — same formula as the backend, for the
  // preview column only; the API remains the authority.
  const charged = (usd: number) => usd / (1 - Math.min(Math.max(marginBps, 0), 9000) / 10_000)

  return (
    <>
      <PageHeader
        title="Rates & models"
        subtitle="What each model costs us, and what a customer pays at a given margin. Changes reach the gateway within a minute."
        action={<Button onClick={() => setAdding((v) => !v)}>{adding ? "Cancel" : "Add model"}</Button>}
      />
      {adding && (
        <AddModelForm
          onDone={() => {
            setAdding(false)
            models.reload()
          }}
        />
      )}
      <div className="flex items-center gap-2 text-sm text-muted">
        Preview margin:
        <input
          type="number"
          min="0"
          max="9000"
          value={marginBps}
          onChange={(e) => setMarginBps(Number(e.target.value))}
          className="w-24 rounded border border-border bg-surface px-2 py-1 text-sm text-foreground outline-none focus:border-interactive"
        />
        bps ({(marginBps / 100).toFixed(1)}%)
      </div>
      <ErrorNote error={models.error} />
      {models.loading ? (
        <Spinner />
      ) : (
        <div className="flex flex-col gap-2">
          {(models.data?.models ?? []).map((m) => (
            <Card key={m.modelKey} className={`flex flex-col p-4 ${m.enabled ? "" : "opacity-60"}`}>
              <button
                type="button"
                className="flex w-full items-center gap-3 text-left"
                onClick={() => setOpen(open === m.modelKey ? null : m.modelKey)}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-foreground">{m.displayName}</span>
                    {m.isDefault && (
                      <span className="rounded border border-success/30 bg-success-soft px-1.5 py-0.5 text-xs text-success">
                        default
                      </span>
                    )}
                    {!m.enabled && (
                      <span className="rounded border border-border px-1.5 py-0.5 text-xs text-muted">off</span>
                    )}
                    <span className="rounded border border-border px-1.5 py-0.5 text-xs text-muted">{m.kind}</span>
                  </div>
                  <p className="truncate font-mono text-xs text-muted">
                    {m.modelKey} · {m.provider}
                  </p>
                </div>
                <div className="hidden text-right text-xs sm:block">
                  <p className="font-mono tabular-nums text-foreground">
                    {perMtok(m.inputUsdPerMtok)} in / {perMtok(m.outputUsdPerMtok)} out
                  </p>
                  <p className="font-mono tabular-nums text-muted">
                    → {perMtok(charged(m.inputUsdPerMtok))} / {perMtok(charged(m.outputUsdPerMtok))}
                  </p>
                </div>
              </button>
              {open === m.modelKey && <ModelEditor model={m} onChanged={models.reload} />}
            </Card>
          ))}
        </div>
      )}
    </>
  )
}

function ModelEditor({ model, onChanged }: { model: PlatformModel; onChanged: () => void }) {
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const num = (name: string) => Number(form.get(name))
    setError(null)
    setSaved(false)
    try {
      await api.patch(`/platform/models/${model.modelKey}`, {
        displayName: form.get("displayName"),
        inputUsdPerMtok: num("input"),
        outputUsdPerMtok: num("output"),
        cachedInputUsdPerMtok: num("cached"),
        ...(model.kind === "chat" && {
          defaultMaxTokens: num("maxTokens"),
          contextWindow: num("contextWindow"),
        }),
      })
      setSaved(true)
      onChanged()
    } catch (err) {
      setError(err)
    }
  }

  async function act(run: () => Promise<unknown>) {
    setError(null)
    try {
      await run()
      onChanged()
    } catch (err) {
      setError(err)
    }
  }

  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-3 border-t border-border pt-4">
      <Field label="Display name" name="displayName" defaultValue={model.displayName} />
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Input $/Mtok" name="input" type="number" step="0.001" min="0" defaultValue={String(model.inputUsdPerMtok)} />
        <Field label="Output $/Mtok" name="output" type="number" step="0.001" min="0" defaultValue={String(model.outputUsdPerMtok)} />
        <Field label="Cached input $/Mtok" name="cached" type="number" step="0.001" min="0" defaultValue={String(model.cachedInputUsdPerMtok)} />
      </div>
      {model.kind === "chat" && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Default max tokens" name="maxTokens" type="number" min="0" defaultValue={String(model.defaultMaxTokens)} />
          <Field
            label="Context window"
            name="contextWindow"
            type="number"
            min="0"
            defaultValue={String(model.contextWindow)}
            hint="Prompt and completion together. 0 means unknown — the chat shows a dash rather than a percentage, and the agent does not auto-compact. Take it from the provider's documentation; a number that is too large drops conversation history."
          />
        </div>
      )}
      {saved && (
        <p className="text-sm text-success">
          Saved. The gateway will use the updated settings within a minute.
        </p>
      )}
      <ErrorNote error={error} />
      <div className="flex flex-wrap gap-2">
        <Button type="submit">Save prices</Button>
        {!model.isDefault && model.enabled && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              // Re-pointing chat is instant and reversible; re-pointing
              // embedding strands every stored vector until pages are
              // re-embedded, so it gets a confirmation the chat switch doesn't.
              if (
                model.kind === "embedding" &&
                !window.confirm(
                  "Switch the embedding model? Existing page vectors stop being searchable until each space is re-embedded, which is billed. Semantic search degrades to keyword search in the meantime.",
                )
              )
                return
              void act(() => api.patch(`/platform/models/${model.modelKey}`, { isDefault: true }))
            }}
          >
            Make default
          </Button>
        )}
        {model.enabled ? (
          !model.isDefault && (
            <Button
              type="button"
              variant="danger"
              onClick={() => act(() => api.patch(`/platform/models/${model.modelKey}`, { enabled: false }))}
            >
              Disable
            </Button>
          )
        ) : (
          <Button
            type="button"
            variant="ghost"
            onClick={() => act(() => api.patch(`/platform/models/${model.modelKey}`, { enabled: true }))}
          >
            Enable
          </Button>
        )}
      </div>
    </form>
  )
}

function AddModelForm({ onDone }: { onDone: () => void }) {
  const [error, setError] = useState<unknown>(null)
  const [kind, setKind] = useState<"chat" | "embedding">("chat")

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const num = (name: string) => Number(form.get(name) || 0)
    setError(null)
    try {
      await api.post("/platform/models", {
        modelKey: form.get("modelKey"),
        kind,
        provider: form.get("provider"),
        apiModel: form.get("apiModel"),
        displayName: form.get("displayName"),
        inputUsdPerMtok: num("input"),
        ...(kind === "chat"
          ? {
              outputUsdPerMtok: num("output"),
              cachedInputUsdPerMtok: num("cached"),
              defaultMaxTokens: num("maxTokens") || 8000,
              contextWindow: num("contextWindow"),
            }
          : { dimensions: num("dimensions") }),
      })
      onDone()
    } catch (err) {
      setError(err)
    }
  }

  return (
    <Card className="p-5">
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Model key" name="modelKey" placeholder="qwen3-235b" required hint="Stable id — never renamed." />
          <Field label="Display name" name="displayName" required />
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">Type</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as "chat" | "embedding")}
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-interactive"
            >
              <option value="chat">chat</option>
              <option value="embedding">embedding</option>
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">Provider</span>
            <select name="provider" className="rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-interactive">
              <option value="fireworks">fireworks</option>
              <option value="together">together</option>
            </select>
          </label>
          <Field label="Provider's model string" name="apiModel" placeholder="accounts/fireworks/models/…" required />
          <Field label="Input $/Mtok" name="input" type="number" step="0.001" min="0" required />
          {kind === "chat" ? (
            <>
              <Field label="Output $/Mtok" name="output" type="number" step="0.001" min="0" required />
              <Field label="Cached input $/Mtok" name="cached" type="number" step="0.001" min="0" defaultValue="0" />
              <Field label="Default max tokens" name="maxTokens" type="number" min="0" defaultValue="8000" />
              <Field
                label="Context window"
                name="contextWindow"
                type="number"
                min="0"
                defaultValue="0"
                hint="Prompt and completion together, from the provider's documentation. Leave 0 if you do not know it — the chat then shows a dash instead of a percentage, which is better than a guess: too large a number makes the agent drop conversation history."
              />
            </>
          ) : (
            <Field
              label="Native dimensions"
              name="dimensions"
              type="number"
              min="2000"
              required
              hint="From the provider's model card. Must be at least 2000 — vectors are stored 2000 wide."
            />
          )}
        </div>
        {kind === "embedding" && (
          <p className="text-xs text-muted">
            Embeddings bill on input only. The new model does nothing until you make it default —
            and that switch means every customer's pages need re-embedding (billed) before semantic
            search sees them again.
          </p>
        )}
        <ErrorNote error={error} />
        <div>
          <Button type="submit">Add model</Button>
        </div>
      </form>
    </Card>
  )
}

// ---------- Analytics ----------

// The one screen that spans tenants and shows what the inference actually cost
// us — the same query builder as the company view, with includeCostBasis on.
export function PlatformAnalyticsPage() {
  const [groupBy, setGroupBy] = useState<string>("company")
  const [days, setDays] = useState<number | null>(30)
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [companyId, setCompanyId] = useState("")
  const [model, setModel] = useState("")
  const [error, setError] = useState<unknown>(null)

  const companies = useLoad(() => api.get<{ companies: PlatformCompany[] }>("/platform/companies"))
  const models = useLoad(() => api.get<{ models: PlatformModel[] }>("/platform/models"))

  const effectiveFrom = days
    ? new Date(Date.now() - (days - 1) * 86_400_000).toISOString().slice(0, 10)
    : from
  const params = new URLSearchParams({ groupBy })
  if (effectiveFrom) params.set("from", effectiveFrom)
  if (!days && to) params.set("to", to)
  if (companyId) params.set("companyId", companyId)
  if (model) params.set("model", model)

  const report = useLoad(
    () => api.get<PlatformReport>(`/platform/analytics?${params}`),
    [params.toString()],
  )
  const rows = report.data?.rows ?? []
  const totals = report.data?.totals

  async function exportCsv() {
    setError(null)
    try {
      const csv = await api.raw(`/platform/analytics/export?${params}`)
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }))
      const a = document.createElement("a")
      a.href = url
      a.download = `platform-analytics-${groupBy}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err)
    }
  }

  return (
    <>
      <PageHeader
        title="Platform analytics"
        subtitle="Across every company. Cost basis and margin are visible here and nowhere else."
        action={<Button variant="ghost" onClick={exportCsv}>Export CSV</Button>}
      />

      <div className="flex flex-wrap items-center gap-2">
        {[7, 30, 90].map((d) => (
          <Button key={d} onClick={() => setDays(d)} className={days === d ? undefined : "bg-surface text-muted"}>
            {d} days
          </Button>
        ))}
        <Button onClick={() => setDays(null)} className={days === null ? undefined : "bg-surface text-muted"}>
          Custom
        </Button>
        {days === null && (
          <>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm outline-none focus:border-interactive" />
            <span className="text-xs text-muted">to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm outline-none focus:border-interactive" />
          </>
        )}
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          className="ml-auto rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-interactive"
        >
          {["company", "day", "model", "kind", "employee", "project"].map((g) => (
            <option key={g} value={g}>
              By {g}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          className="rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-interactive"
        >
          <option value="">All companies</option>
          {(companies.data?.companies ?? []).map((co) => (
            <option key={co.id} value={co.id}>
              {co.name}
            </option>
          ))}
        </select>
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-interactive"
        >
          <option value="">All models</option>
          {(models.data?.models ?? []).map((m) => (
            <option key={m.modelKey} value={m.modelKey}>
              {m.displayName}
            </option>
          ))}
        </select>
      </div>

      <ErrorNote error={error ?? report.error} />

      {report.loading ? (
        <Spinner />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Charged" value={totals?.chargedFormatted ?? "—"} />
            <Stat label="Cost" value={money(totals?.costMicroUsd)} />
            <Stat label="Margin" value={money(totals?.marginMicroUsd)} />
          </div>

          {rows.length ? (
            <Card className="flex flex-col gap-1 p-4 text-sm">
              <div className="flex items-center gap-3 text-xs text-faint">
                <span className="w-48 shrink-0">{groupBy}</span>
                <span className="flex-1 text-right">charged</span>
                <span className="w-24 text-right">cost</span>
                <span className="w-24 text-right">margin</span>
              </div>
              {rows.map((r, i) => (
                <div key={`${r.key}-${r.label}`} className="flex items-center gap-3">
                  <span className="w-48 shrink-0 truncate text-muted">
                    {/* Rows arrive sorted by charge, so for people this is the
                        contributor ranking. */}
                    {groupBy === "employee" && <span className="mr-1 text-faint">{i + 1}.</span>}
                    {r.label}
                  </span>
                  <span className="flex-1 text-right font-mono text-xs tabular-nums text-foreground">
                    {r.chargedFormatted}
                  </span>
                  <span className="w-24 text-right font-mono text-xs tabular-nums text-muted">
                    {money(r.costMicroUsd)}
                  </span>
                  <span className="w-24 text-right font-mono text-xs tabular-nums text-success">
                    {money(r.marginMicroUsd)}
                  </span>
                </div>
              ))}
            </Card>
          ) : (
            <Empty title="No usage in this range" />
          )}
        </>
      )}
    </>
  )
}

// ---------- Audit ----------

export function PlatformAuditPage() {
  const audit = useLoad(() => api.get<{ actions: AdminActionEntry[] }>("/platform/audit?limit=200"))

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Every action staff took, newest first. Append-only."
      />
      <ErrorNote error={audit.error} />
      <AuditList actions={audit.data?.actions ?? []} loading={audit.loading} />
    </>
  )
}

function AuditList({ actions, loading }: { actions: AdminActionEntry[]; loading: boolean }) {
  if (loading) return <Spinner />
  if (!actions.length) return <Empty title="No staff actions yet" />
  return (
    <Card className="flex flex-col divide-y divide-border">
      {actions.map((a) => (
        <div key={a.id} className="flex items-start gap-3 px-4 py-3 text-sm">
          <div className="min-w-0 flex-1">
            <p className="text-foreground">
              <span className="font-mono text-xs">{a.action}</span>
              <span className="ml-2 text-xs text-muted">by {a.actorName ?? a.actorEmail ?? "unknown"}</span>
            </p>
            {Object.keys(a.payload ?? {}).length > 0 && (
              <p className="mt-0.5 truncate font-mono text-xs text-muted" title={JSON.stringify(a.payload)}>
                {JSON.stringify(a.payload)}
              </p>
            )}
          </div>
          <span className="shrink-0 text-xs text-faint">{formatDateTime(a.createdAt)}</span>
        </div>
      ))}
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className="text-xs text-muted">{label}</span>
      <span className="font-mono text-xl font-semibold tabular-nums text-foreground">{value}</span>
    </Card>
  )
}
